import { SetupGate } from "./components/SetupGate";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from 'sonner';
import { ConfirmHost } from './components/ConfirmHost';
import LoginPage from "./pages/LoginPage";
import VerifyPermitPage from "./pages/VerifyPermitPage";
import DashboardPage from "./pages/DashboardPage";
import LocationsPage from "./pages/LocationsPage";
import CompaniesPage from "./pages/CompaniesPage";
import EmployeesPage from "./pages/EmployeesPage";
import EquipmentPage from "./pages/EquipmentPage";
import EqTypesPage from "./pages/EqTypesPage";
import WorkPermitsPage from "./pages/WorkPermitsPage";
import WorkPermitWizardPage from "./pages/WorkPermitWizardPage";
import WorkAreasPage from "./pages/WorkAreasPage";
import JobTypesPage from "./pages/JobTypesPage";
import JobKindsPage from "./pages/JobKindsPage";
import TemplatesPage from "./pages/TemplatesPage";
import ReportsPage from "./pages/ReportsPage";
import EmailSettingsPage from "./pages/EmailSettingsPage";
import UsersPage from "./pages/UsersPage";
import ProfilePage from "./pages/ProfilePage";
import SystemSettingsPage from "./pages/SystemSettingsPage";
import IncomingPermitsPage from "./pages/IncomingPermitsPage";
import FiveStarPage from "./pages/FiveStarPage";
import FiveStarAuditPage from "./pages/FiveStarAuditPage";
import FiveStarCriteriaPage from "./pages/FiveStarCriteriaPage";
import FirmEvaluationPage from "./pages/FirmEvaluationPage";
import CompanyStatusesPage from "./pages/CompanyStatusesPage";
import CompanyRegisterPage from "./pages/CompanyRegisterPage";
import FirmDashboardPage from "./pages/FirmDashboardPage";
import FirmFiveStarPage from "./pages/FirmFiveStarPage";
import UserGuidePage from "./pages/UserGuidePage";
import PromoPage from "./pages/PromoPage";
import FirmGuidePage from "./pages/FirmGuidePage";
import FirmStatusPage from "./pages/FirmStatusPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import Layout from "./components/Layout";
import { HealthMaskHost } from "./components/health-mask/HealthMaskEditor";
import FirmAccessPage from './pages/FirmAccessPage';
import FirmAccessHistoryPage from './pages/FirmAccessHistoryPage';
import { AuthProvider, useAuth } from "./context/AuthContext";
import IsgCommitmentsPage from './pages/IsgCommitmentsPage';
import SpecialSituationsPage from './pages/SpecialSituationsPage';
import TimeRulesPage from './pages/TimeRulesPage';
import LoginAnnouncementsPage from './pages/LoginAnnouncementsPage';
import UserAnnouncementsPage from './pages/UserAnnouncementsPage';
import FirmAnnouncementsPage from './pages/FirmAnnouncementsPage';
import DocLibraryPage from './pages/DocLibraryPage';
import StoresPage from './pages/StoresPage';
import { ForcePasswordChange } from './components/ForcePasswordChange';
import FirmDocumentsPage from './pages/FirmDocumentsPage';
import PlatformOwnerPage from './pages/PlatformOwnerPage';
import { MobileTableLabels } from './components/MobileTableLabels';
import { LicenseProvider, useLicense } from './lib/license';
import { LicenseExpiredScreen } from './components/LicenseExpiredScreen';

// Lisans süresi dolduysa (platform sahibi paneli hariç) her yerde lisans ekranı
const LicenseGate = ({ children }) => {
  const { status } = useLicense();
  const { pathname } = useLocation();
  if (status && status.active === false && !pathname.startsWith('/platform-owner')) return <LicenseExpiredScreen />;
  return children;
};

// Kapalı eklentinin sayfaları açılmaz
const AddonRoute = ({ addon, children }) => {
  const { hasAddon } = useLicense();
  return hasAddon(addon) ? children : <Navigate to="/" replace />;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!user) {
    if (window.location.search.includes('focus=')) sessionStorage.setItem('after_login', window.location.pathname + window.location.search);
    return <Navigate to="/login" replace />;
  }
  if (user.must_change_password) return <ForcePasswordChange />;
  
  return children;
};

// Yetki kontrolü ile route
const PermissionRoute = ({ children, module }) => {
  const { canView, user } = useAuth();
  
  if (!user) return <Navigate to="/login" replace />;
  
  // Super admin her şeyi görebilir
  if (user.is_super_admin) return children;
  
  // Modül yetkisi kontrol et
  if (module && !canView(module)) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <div className="text-6xl mb-4">🚫</div>
        <h2 className="text-xl font-semibold text-slate-800 mb-2">Erişim Engellendi</h2>
        <p className="text-slate-500">Bu sayfayı görüntüleme yetkiniz bulunmamaktadır.</p>
      </div>
    );
  }
  
  return children;
};

// Firma yetkilisi kendi sade panelini görür
const DashboardSwitch = () => {
  const { user } = useAuth();
  if (user?.role === 'firma_yetkilisi') return <FirmDashboardPage />;
  return <PermissionRoute module="dashboard"><DashboardPage /></PermissionRoute>;
};

function App() {
  return (
    <LicenseProvider>
    <AuthProvider>
      <BrowserRouter>
        <SetupGate>
        <LicenseGate>
        <Toaster position="top-right" richColors closeButton />
        <ConfirmHost />
        <HealthMaskHost />
        <MobileTableLabels />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/platform-owner" element={<PlatformOwnerPage />} />
          <Route path="/dogrula/:permitId" element={<VerifyPermitPage />} />
          <Route path="/register-company" element={<CompanyRegisterPage />} />
          <Route path="/tanitim" element={<PromoPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardSwitch />} />
            <Route path="locations" element={<PermissionRoute module="locations"><LocationsPage /></PermissionRoute>} />
            <Route path="companies" element={<PermissionRoute module="companies"><CompaniesPage /></PermissionRoute>} />
            <Route path="employees" element={<PermissionRoute module="employees"><EmployeesPage /></PermissionRoute>} />
            <Route path="equipment" element={<PermissionRoute module="equipment"><EquipmentPage /></PermissionRoute>} />
            <Route path="equipment-types" element={<PermissionRoute module="equipment"><EqTypesPage /></PermissionRoute>} />
            <Route path="work-permits" element={<PermissionRoute module="work_permits"><WorkPermitsPage /></PermissionRoute>} />
            <Route path="work-permits/new" element={<PermissionRoute module="work_permits"><WorkPermitWizardPage /></PermissionRoute>} />
            <Route path="work-permits/:permitId/edit" element={<PermissionRoute module="work_permits"><WorkPermitWizardPage /></PermissionRoute>} />
            <Route path="work-areas" element={<PermissionRoute module="work_areas"><WorkAreasPage /></PermissionRoute>} />
            <Route path="job-types" element={<PermissionRoute module="work_areas"><JobTypesPage /></PermissionRoute>} />
            <Route path="job-kinds" element={<PermissionRoute module="work_areas"><JobKindsPage /></PermissionRoute>} />
            <Route path="templates" element={<PermissionRoute module="templates"><TemplatesPage /></PermissionRoute>} />
            <Route path="reports" element={<PermissionRoute module="companies"><ReportsPage /></PermissionRoute>} />
            <Route path="email-settings" element={<PermissionRoute module="settings"><EmailSettingsPage /></PermissionRoute>} />
            <Route path="users" element={<PermissionRoute module="users"><UsersPage /></PermissionRoute>} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="firma-durumu" element={<FirmStatusPage />} />
            <Route path="system-settings" element={<PermissionRoute module="system_settings"><SystemSettingsPage /></PermissionRoute>} />
            <Route path="company-statuses" element={<PermissionRoute module="company_statuses"><CompanyStatusesPage /></PermissionRoute>} />
            <Route path="isg-taahhutname" element={<PermissionRoute module="isg_commitments"><IsgCommitmentsPage /></PermissionRoute>} />
            <Route path="ozel-durumlar" element={<PermissionRoute module="special_situations"><SpecialSituationsPage /></PermissionRoute>} />
            <Route path="talep-zaman-kurallari" element={<PermissionRoute module="time_rules"><TimeRulesPage /></PermissionRoute>} />
            <Route path="giris-duyurulari" element={<PermissionRoute module="login_announcements"><LoginAnnouncementsPage /></PermissionRoute>} />
            <Route path="kullanici-duyurulari" element={<PermissionRoute module="user_announcements"><UserAnnouncementsPage /></PermissionRoute>} />
            <Route path="duyurularim" element={<FirmAnnouncementsPage />} />
            <Route path="dokumantasyon" element={<PermissionRoute module="doc_library"><DocLibraryPage /></PermissionRoute>} />
            <Route path="magazalar" element={<PermissionRoute module="stores"><StoresPage /></PermissionRoute>} />
            <Route path="dokumanlar" element={<FirmDocumentsPage />} />
            <Route path="five-star-degerlendirme" element={<AddonRoute addon="five_star"><FirmFiveStarPage /></AddonRoute>} />
            <Route path="kullanim-kilavuzu" element={<PermissionRoute module="user_guide"><UserGuidePage /></PermissionRoute>} />
            <Route path="firma-kilavuzu" element={<FirmGuidePage />} />
            <Route path="gelen-talepler" element={<IncomingPermitsPage />} />
            <Route path="firma-erisimi" element={<FirmAccessPage />} />
            <Route path="firma-erisim-gecmisi" element={<FirmAccessHistoryPage />} />
            <Route path="five-star" element={<AddonRoute addon="five_star"><PermissionRoute module="five_star"><FiveStarPage /></PermissionRoute></AddonRoute>} />
            <Route path="five-star/denetle/:permitId" element={<AddonRoute addon="five_star"><PermissionRoute module="five_star"><FiveStarAuditPage /></PermissionRoute></AddonRoute>} />
            <Route path="five-star-basliklar" element={<AddonRoute addon="five_star"><PermissionRoute module="five_star_criteria"><FiveStarCriteriaPage /></PermissionRoute></AddonRoute>} />
            <Route path="firma-degerlendirme" element={<AddonRoute addon="five_star"><PermissionRoute module="firm_evaluation"><FirmEvaluationPage /></PermissionRoute></AddonRoute>} />
          </Route>
        </Routes>
        </LicenseGate>
      </SetupGate>
      </BrowserRouter>
    </AuthProvider>
    </LicenseProvider>
  );
}

export default App;
